import type { StrategyEditorSectionId } from '../strategyEditorSections';
import { isVisibleTradingStrategyLeg, type TradingStrategyFormData } from '../tradingStrategyEditorSubmitPayloads';

/**
 * Maps a validation error key back to the section that owns the field. Fields now live behind
 * sections, so a failed save has to be able to reveal the offending input rather than silently
 * rejecting on a pane the user cannot see.
 */
export const resolveStrategyEditorSectionForValidationErrors = (
    validationErrors: Record<string, string>
): StrategyEditorSectionId | null => {
    const errorKeys = Object.keys(validationErrors).filter((errorKey) => validationErrors[errorKey] !== '');

    if (errorKeys.length === 0) return null;

    // Ordered by how early the field appears in the editor, so the user is sent to the first problem.
    const orderedSectionMatchers: { section: StrategyEditorSectionId; matches: (errorKey: string) => boolean }[] = [
        {
            section: 'setup',
            matches: (errorKey) =>
                errorKey === 'name' ||
                errorKey === 'description' ||
                errorKey === 'allowedUnderlyingEntityTypes' ||
                errorKey.startsWith('sessionTimingPolicy_')
        },
        { section: 'position', matches: (errorKey) => errorKey.startsWith('leg_') },
        { section: 'backtests', matches: (errorKey) => errorKey === 'autoBacktestOptionBuyingStrategiesOverride' }
    ];

    for (const matcher of orderedSectionMatchers) {
        if (errorKeys.some(matcher.matches)) return matcher.section;
    }

    return null;
};

export interface CoverageTone {
    /** Background + text pair for chips and pills. */
    chipClass: string;
    /** Fill colour for the coverage meter. */
    barClass: string;
}

/** Shared coverage thresholds so the header chip and the overview meter can never disagree. */
export const getCoverageTone = (coveragePercent: number): CoverageTone => {
    if (coveragePercent >= 80) return { chipClass: 'bg-green-900/50 text-green-300', barClass: 'bg-green-500' };
    if (coveragePercent >= 40) return { chipClass: 'bg-yellow-900/50 text-yellow-300', barClass: 'bg-yellow-500' };

    return { chipClass: 'bg-red-900/50 text-red-300', barClass: 'bg-red-500' };
};

export const buildTradingStrategyValidationErrors = (formData: TradingStrategyFormData): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
        errors.name = 'Strategy name is required';
    }

    if (!formData.description.trim()) {
        errors.description = 'Strategy description is required';
    }

    if (formData.allowedUnderlyingEntityTypes.length === 0) {
        errors.allowedUnderlyingEntityTypes = 'At least one entity type must be selected';
    }
    if (
        formData.allowedUnderlyingEntityTypes.includes('AssetIndex') &&
        formData.active &&
        (formData.allowedUnderlyingEntityTypes.length !== 1 || !formData.entityListId || !formData.afterHoursRemoved)
    ) {
        errors.allowedUnderlyingEntityTypes = 'Active index-option strategies require an index-only entity list and regular-session bars';
    }

    if (
        formData.allowedUnderlyingEntityTypes.includes('FuturesProduct') &&
        (formData.allowedUnderlyingEntityTypes.length !== 1 ||
            formData.active ||
            formData.autoBacktest ||
            formData.afterHoursRemoved ||
            formData.duration !== '1m')
    ) {
        errors.allowedUnderlyingEntityTypes = 'Futures research requires a product-only inactive 1m strategy using exchange sessions';
    }

    const strategyLevelOverridesCount = formData.autoBacktestOptionBuyingStrategiesOverride?.length ?? 0;
    const optionLegsMissingConfig: number[] = [];

    formData.legs.forEach((leg, index) => {
        if (!isVisibleTradingStrategyLeg(leg)) return;

        if (!leg.name.trim()) {
            errors[`leg_${index}_name`] = 'Leg name is required';
        }

        if (leg.entryTransactionSizeValue <= 0) {
            errors[`leg_${index}_entryTransactionSizeValue`] = 'Transaction size must be greater than 0';
        }

        if (leg.entryTransactionSizeValue > 100) {
            errors[`leg_${index}_entryTransactionSizeValue`] = 'Transaction size must be 100 or less';
        }

        const hasOptionAction = leg.openTransactionType === 'buyToOpen' || leg.openTransactionType === 'sellToOpen';
        if (leg.derivativeType === null ? hasOptionAction : !hasOptionAction) {
            errors[`leg_${index}_openTransactionType`] = 'Choose an opening action for the selected instrument';
        }

        if (leg.derivativeType !== 'Option' && leg.optionType !== null) {
            errors[`leg_${index}_optionType`] = 'Only option legs can specify Call or Put';
        }

        if (leg.derivativeType === 'Option') {
            if (leg.optionType === null) {
                errors[`leg_${index}_optionType`] = 'Select Call or Put';
            }

            const legLevelOverridesCount = leg.autoBacktestOptionBuyingStrategiesOverride?.length ?? 0;

            if (strategyLevelOverridesCount === 0 && legLevelOverridesCount === 0) {
                optionLegsMissingConfig.push(index);
            }
        }
    });

    if (optionLegsMissingConfig.length > 0) {
        errors.autoBacktestOptionBuyingStrategiesOverride =
            'Option legs require option buying strategies. Set a strategy default, or set per-leg overrides for each option leg.';

        for (const index of optionLegsMissingConfig) {
            errors[`leg_${index}_autoBacktestOptionBuyingStrategiesOverride`] =
                'Select at least one option buying strategy, or use the strategy default.';
        }
    }

    // Every duration uses the same near-close offset contract.
    Object.entries(formData.sessionTimingPolicy ?? {}).forEach(([dur, policy]) => {
        if (!policy) return;
        const usesNearClose = policy.detectionCutoff === 'nearClose' || policy.executionPolicy === 'nearClose';

        if (usesNearClose) {
            const v = policy.preCloseOffsetSecs;
            const isMissing = v === undefined || v === null || Number.isNaN(Number(v));
            const isNegative = typeof v === 'number' && v < 0;
            const exceedsSupportedLead = typeof v === 'number' && v > 120;

            if (isMissing || isNegative) {
                errors[`sessionTimingPolicy_${dur}_preCloseOffsetSecs`] = 'Pre-close offset (secs) is required for Near Close';
            } else if (exceedsSupportedLead) {
                errors[`sessionTimingPolicy_${dur}_preCloseOffsetSecs`] = 'Near-close offset cannot exceed 120 seconds';
            }
        }
    });

    return errors;
};
